import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    Alert,
    InputAdornment,
} from '@mui/material';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { motion } from 'framer-motion';

const ViewCategoryDealCreate = ({ open, onClose, onDealCreated }) => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [bannerData, setBannerData] = useState({
        image: null,
        visit_link: '',
        status: 'approved',
        brand_name: '',
        category: '',
        keywords: '',
    });
    const [initialBannerData] = useState({
        status: 'approved',
        brand_name: '',
        category: '',
        keywords: '',
    });
    const [error, setError] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchBanners();
        fetchBrands();
        fetchCategories();
    }, []);

    useEffect(() => {
        if (open) {
            setBannerData({
                image: null,
                visit_link: '',
                status: 'approved',
                brand_name: '',
                category: '',
                keywords: '',
            });
            setImagePreview(null);
            setError(null);
        }
    }, [open]);

    useEffect(() => {
        if (bannerData.image) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(bannerData.image);
        } else {
            setImagePreview(null);
        }
    }, [bannerData.image]);

    const fetchBanners = async () => {
        try {
            const response = await fetch('https://stage.socioglamm.com/clientbanner');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setBanners(Array.isArray(data) ? data.reverse() : []);
        } catch (error) {
            console.error('Error fetching banners:', error);
            setError('Failed to fetch banners.');
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await fetch('https://stage.socioglamm.com/brands');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setBrands(Array.isArray(data?.brands) ? data.brands : []);
        } catch (error) {
            console.error('Error fetching brands:', error);
            setError('Failed to fetch brands.');
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch('https://stage.socioglamm.com/categories');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setCategories(Array.isArray(data?.categories) ? data.categories : []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setError('Failed to fetch categories.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBannerData({ ...bannerData, [name]: value });
    };

    const handleFileChange = (e) => {
        setBannerData({ ...bannerData, image: e.target.files[0] });
    };

    const handleBannerUpload = async (e) => {
        e.preventDefault();

        if (!bannerData.image) {
            setError('Please select an image.');
            return;
        }
        const allowedTypes = ['image/jpeg', 'image/png'];
        if (!allowedTypes.includes(bannerData.image.type)) {
            setError('Please upload a JPEG or PNG image.');
            return;
        }
        if (bannerData.image.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB.');
            return;
        }
        if (!bannerData.visit_link || !/^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(bannerData.visit_link)) {
            setError('Please provide a valid URL for the visit link.');
            return;
        }
        if (!bannerData.brand_name) {
            setError('Please select a brand.');
            return;
        }
        if (!bannerData.category) {
            setError('Please provide a category.');
            return;
        }
        if (!bannerData.keywords || bannerData.keywords.trim() === '') {
            setError('Please provide at least one keyword.');
            return;
        }

        const formData = new FormData();
        formData.append('image', bannerData.image);
        formData.append('visit_link', bannerData.visit_link);
        formData.append('status', bannerData.status);
        formData.append('brand_name', bannerData.brand_name);
        formData.append('category', bannerData.category);
        const keywordsArray = bannerData.keywords
            .split(',')
            .map((k) => k.trim())
            .filter((k) => k !== '');
        formData.append('keywords', JSON.stringify(keywordsArray));

        try {
            setLoading(true);
            setError(null);
            const response = await fetch('https://stage.socioglamm.com/clientbanner', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                console.error('Server response:', result);
                throw new Error(result.message || `HTTP error! Status: ${response.status}`);
            }

            onDealCreated(result);
            setBannerData({
                image: null,
                visit_link: '',
                status: 'approved',
                brand_name: '',
                category: '',
                keywords: '',
            });
            setImagePreview(null);
            onClose();
        } catch (error) {
            console.error('Error uploading banner:', error);
            setError(error.message || 'Failed to create deal.');
        } finally {
            setLoading(false);
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8, y: -50 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
        exit: { opacity: 0, scale: 0.8, y: '0, visibility: hidden' },
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="add-deal-modal-title">
            <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                style={{
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)',
                    top: '5%',
                    left: '30%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #e0e0e0',
                    background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                    height: '80vh',
                    overflowY: 'auto',
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        pb: 1,
                        borderBottom: '1px solid #e0e0e0'
                    }}>
                        <Typography id="add-deal-modal-title" variant="h5" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            Add New Deal
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleBannerUpload}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: "auto",
                                        border: '2px dashed #2e7d32',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#f5f5f5',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {imagePreview ? (
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#757575' }}>
                                            No image selected
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <TextField
                                id="banner-image-upload"
                                type="file"
                                accept="image/*"
                                label="Banner Image"
                                onChange={handleFileChange}
                                InputLabelProps={{ sx: { color: '#2e7d32' } }}
                                inputProps={{ sx: { py: 1.5 } }}
                                helperText={bannerData.image ? bannerData.image.name : 'No file chosen'}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ImageSearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        color: '#2e7d32',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2e7d32',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2e7d32',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2e7d32',
                                        },
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#2e7d32',
                                        '& fieldset': { borderColor: '#2e7d32' },
                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#2e7d32',
                                        '&.Mui-focused': { color: '#2e7d32' }
                                    }
                                }}
                            />
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Visit Link"
                            name="visit_link"
                            value={bannerData.visit_link}
                            onChange={handleInputChange}
                            ovil placeholder="e.g., https://example.com/deal"
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: '#2e7d32',
                                    '& fieldset': { borderColor: '#2e7d32' },
                                    '&:hover fieldset': { borderColor: '#2e7d32' },
                                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#2e7d32',
                                    '&.Mui-focused': { color: '#2e7d32' }
                                }
                            }}
                            required
                            InputLabelProps={{ sx: { color: '#2e7d32' } }}
                        />

                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Keywords (comma-separated)"
                                name="keywords"
                                value={bannerData.keywords}
                                onChange={handleInputChange}
                                placeholder="e.g., makeup, skincare, accessories"
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#2e7d32',
                                        '& fieldset': { borderColor: '#2e7d32' },
                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#2e7d32',
                                        '&.Mui-focused': { color: '#2e7d32' }
                                    }
                                }}
                                InputLabelProps={{
                                    style: { color: '#2e7d32' },
                                    sx: {
                                        '&.Mui-focused': {
                                            color: '#2e7d32',
                                        },
                                    },
                                    color: "#2e7d32"
                                }}
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(bannerData.keywords.split(',').map((item) => item.trim()).filter(Boolean)).map((keyword, idx) => (
                                    <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: 'green', borderRadius: 1, fontSize: 13 }}>
                                        {keyword}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <TextField
                            select
                            label="Category"
                            name="category"
                            value={bannerData.category}
                            onChange={handleInputChange}
                            fullWidth
                            size="small"
                            required
                            variant="outlined"
                            disabled={loading}
                            InputLabelProps={{
                                style: { color: '#2e7d32' },
                                sx: {
                                    '&.Mui-focused': {
                                        color: '#2e7d32',
                                    },
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#2e7d32' },
                                    '&:hover fieldset': { borderColor: '#2e7d32' },
                                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                },
                                mb: 2,
                            }}
                        >
                            {categories.map((category) => (
                                <MenuItem key={category._id} value={category.name}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="brand-name-label" sx={{ color: '#2e7d32', '&.Mui-focused': { color: '#2e7d32' } }}>
                                Brand Name
                            </InputLabel>
                            <Select
                                labelId="brand-name-label"
                                name="brand_name"
                                value={bannerData.brand_name}
                                onChange={handleInputChange}
                                label="Brand Name"
                                required
                                variant="outlined"
                                disabled={loading}
                                sx={{
                                    color: '#2e7d32',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#2e7d32',
                                    }
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select a brand</em>
                                </MenuItem>
                                {brands.length > 0 ? (
                                    brands.map((brand) => (
                                        <MenuItem key={brand?._id} value={brand?.brand_name}>
                                            {brand?.brand_name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No brands available</MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="status-label" sx={{ color: '#2e7d32', '&.Mui-focused': { color: '#2e7d32' } }}>
                                Status
                            </InputLabel>
                            <Select
                                labelId="status-label"
                                name="status"
                                value={bannerData.status}
                                onChange={handleInputChange}
                                label="Status"
                                sx={{
                                    color: '#2e7d32',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2e7d32',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#2e7d32',
                                    }
                                }}
                            >
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            pt: 2,
                        }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                disabled={loading}
                                sx={{
                                    borderColor: '#bdbdbd',
                                    color: '#757575',
                                    '&:hover': { borderColor: '#616161', color: '#424242' }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                sx={{
                                    backgroundColor: '#4caf50',
                                    '&:hover': { backgroundColor: '#388e3c' },
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Deal'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </motion.div>
        </Modal>
    );
};

export default ViewCategoryDealCreate;